/**
 * For Parents is organized by SERVICE CATEGORY — the thing a parent is
 * looking for — rather than by life stage. People arrive here with a
 * specific errand ("find a photographer", "compare preschools"), so the
 * categories are the doorways.
 */

export const parentsIntro = {
  count: '86 providers · 8 categories',
  updated: 'Verified quarterly · Last checked July 2026',
  deck:
    'Schools, childcare, photographers, health providers, support services and the groups worth joining. Everything below has been called or visited in the last three months, and free or sliding-scale options are always listed first.',
}

export interface ServiceCategory {
  slug: string
  name: string
  count: string
  blurb: string
  swatch: string
}

export const serviceCategories: ServiceCategory[] = [
  {
    slug: 'preschools-daycares-elementary-santa-barbara',
    name: 'Preschools, daycares & elementary',
    count: '48 schools',
    blurb: 'Every school in the county, compared on cost, hours and waitlist.',
    swatch: 'bg-peach',
  },
  {
    slug: 'childcare-babysitters-santa-barbara',
    name: 'Childcare & babysitters',
    count: '11 options',
    blurb: 'Nanny shares, drop-in care, date-night sitters and agencies.',
    swatch: 'bg-opal',
  },
  {
    slug: 'family-photographers-santa-barbara',
    name: 'Family photographers',
    count: '14 photographers',
    blurb: 'Newborn, milestone and beach sessions, with real price ranges.',
    swatch: 'bg-creme',
  },
  {
    slug: 'parent-groups-santa-barbara',
    name: 'Parent groups & meetups',
    count: '19 groups',
    blurb: 'The ones that actually meet. We checked every single one.',
    swatch: 'bg-foam',
  },
  {
    slug: 'pregnancy-postpartum-santa-barbara',
    name: 'Pregnancy & postpartum',
    count: '14 providers',
    blurb: 'Doulas, lactation help, birth prep and new-parent groups.',
    swatch: 'bg-opal',
  },
  {
    slug: 'kids-health-therapy-santa-barbara',
    name: 'Kids health & therapy',
    count: '22 providers',
    blurb: 'Pediatricians, dentists, speech, OT and pediatric therapy.',
    swatch: 'bg-foam',
  },
  {
    slug: 'family-support-counseling-santa-barbara',
    name: 'Support & counseling',
    count: '16 services',
    blurb: 'Family therapy, special needs advocacy and free county programs.',
    swatch: 'bg-creme',
  },
  {
    slug: 'gyms-with-childcare-santa-barbara',
    name: 'Fitness with childcare',
    count: '12 gyms',
    blurb: 'Minimum ages, time limits and what membership really costs.',
    swatch: 'bg-peach',
  },
]

export interface ResourceGuide {
  slug: string
  title: string
  meta: string
  blurb: string
}

export const resourceGuides: ResourceGuide[] = [
  {
    slug: 'preschools-daycares-santa-barbara',
    title: 'Every preschool & daycare, compared',
    meta: '48 schools',
    blurb:
      'Cost, ages, hours, philosophy and waitlist reality in one table. The most-used page on this site in spring.',
  },
  {
    slug: 'family-photographers-santa-barbara',
    title: 'The family photographer guide',
    meta: '14 photographers',
    blurb:
      'Styles side by side with honest price ranges, plus the best months and locations for a beach session.',
  },
  {
    slug: 'mom-groups-santa-barbara',
    title: 'Parent groups that actually meet',
    meta: '19 groups',
    blurb:
      'We contacted every one. The dead Facebook groups are not on this list.',
  },
  {
    slug: 'free-parenting-resources-santa-barbara',
    title: 'Free & sliding-scale support',
    meta: '23 services',
    blurb:
      'County programs, free classes and sliding-scale therapy, in one place.',
  },
]

export interface Provider {
  id: string
  name: string
  category: string
  area: string
  cost: string
  ages: string
  blurb: string
  access: string
  tags: string[]
}

export const providers: Provider[] = [
  {
    id: 'p1',
    name: 'SBCC Parenting Program',
    category: 'Parent groups',
    area: 'Multiple campuses',
    cost: 'Free',
    ages: '0–5',
    blurb:
      'Free drop-in parent-child classes through the community college, birth through pre-K. The single best-kept secret in Santa Barbara parenting, and it costs nothing.',
    access: 'Drop in, no registration',
    tags: ['Parent groups', 'Free', '0–2', 'Drop-in'],
  },
  {
    id: 'p2',
    name: 'Montessori Center School',
    category: 'Schools',
    area: 'Goleta',
    cost: '$$$',
    ages: '18 months–14',
    blurb:
      'The largest Montessori campus in the county, running toddler through middle school. Tours fill by December for the following fall.',
    access: 'Tour, then application',
    tags: ['Schools', '0–2', '3–5'],
  },
  {
    id: 'p3',
    name: 'Kelsey Crews Photography',
    category: 'Photographers',
    area: 'Countywide',
    cost: '$$',
    ages: 'All',
    blurb:
      'Warm, unposed beach and at-home sessions. Books three to four months out for golden-hour slots in summer.',
    access: 'Inquire by email',
    tags: ['Photographers'],
  },
  {
    id: 'p4',
    name: 'Mamatoto Santa Barbara',
    category: 'Pregnancy & postpartum',
    area: 'Downtown',
    cost: '$$',
    ages: 'Pregnancy–1',
    blurb:
      'Birth prep, postpartum groups and lactation consults under one roof. The new-parent group is where a lot of lasting friendships around here start.',
    access: 'Register by session',
    tags: ['Pregnancy & postpartum', 'Parent groups', '0–2'],
  },
  {
    id: 'p5',
    name: 'CALM (Child Abuse Listening Mediation)',
    category: 'Support & counseling',
    area: 'Countywide',
    cost: 'Sliding scale',
    ages: 'All',
    blurb:
      'Family therapy and parenting support regardless of ability to pay. Despite the name, most of what they do is ordinary family counseling.',
    access: 'Intake call, waitlist varies',
    tags: ['Support & counseling', 'Sliding scale', 'Free'],
  },
  {
    id: 'p6',
    name: 'Alpha Resource Center',
    category: 'Support & counseling',
    area: 'Goleta',
    cost: 'Free',
    ages: 'All',
    blurb:
      'Advocacy, IEP support and family navigation for children with developmental disabilities. Start here before you start anywhere else.',
    access: 'Free intake, no referral needed',
    tags: ['Support & counseling', 'Free'],
  },
  {
    id: 'p7',
    name: 'Santa Barbara Family YMCA',
    category: 'Fitness with childcare',
    area: 'Downtown',
    cost: '$$',
    ages: '6 months–12',
    blurb:
      'Two hours of childcare included with membership, which is the real product. Sliding-scale rates exist and are not advertised — ask at the desk.',
    access: 'Membership, financial aid available',
    tags: ['Fitness with childcare', 'Sliding scale', '0–2'],
  },
  {
    id: 'p8',
    name: 'Santa Barbara Pediatric Therapy',
    category: 'Health & therapy',
    area: 'Uptown',
    cost: '$$ / insurance',
    ages: '0–12',
    blurb:
      'Speech, occupational and feeding therapy in one practice. They take most insurance and will tell you honestly if you do not need them.',
    access: 'Referral helpful, not required',
    tags: ['Health & therapy', '0–2', '3–5'],
  },
]

export const parentsTier3 = [
  {
    area: 'Schools & childcare',
    items: [
      'Santa Barbara Family School',
      'La Cumbre Preschool',
      'Storyteller Children’s Center',
      'Goleta Union preschools',
    ],
  },
  {
    area: 'Groups & meetups',
    items: [
      'SB Moms Facebook group',
      'MOPS Santa Barbara',
      'Dad’s Club SB',
      'Goleta Stroller Walks',
    ],
  },
  {
    area: 'Health & therapy',
    items: [
      'Sansum pediatrics',
      'Cottage Children’s Medical Center',
      'SB Pediatric Dentistry',
      'Family Service Agency',
    ],
  },
  {
    area: 'County & free services',
    items: [
      'First 5 Santa Barbara County',
      'WIC Santa Barbara',
      'Public Health nurse line',
      'SB County Early Start',
    ],
  },
]

export const parentsFilters = [
  {
    group: 'Category',
    options: [
      'Schools',
      'Photographers',
      'Parent groups',
      'Pregnancy & postpartum',
      'Health & therapy',
      'Support & counseling',
      'Fitness with childcare',
    ],
  },
  { group: 'Cost', options: ['Free', 'Sliding scale'] },
  { group: 'Age', options: ['0–2', '3–5'] },
]

export const parentsFaqs = [
  {
    question: 'What free parenting resources are there in Santa Barbara?',
    answer:
      'More than most people realize. SBCC Parenting Program runs free drop-in classes birth through pre-K. First 5 and the county Public Health nurse line are free. CALM offers family therapy on a sliding scale that goes to zero. Alpha Resource Center is free for families navigating a diagnosis.',
  },
  {
    question: 'When should I get on a preschool waitlist here?',
    answer:
      'Earlier than feels reasonable. The competitive programs open lists in January for the following September, and a few take names at birth. If your child will be two and a half or older next fall, tour this winter.',
  },
  {
    question: 'How much does a family photographer cost in Santa Barbara?',
    answer:
      'Most mini sessions run $350–$550, and full sessions with a gallery of edited images run $650–$1,200. Beach sessions book earliest for September and October, when the light is best and the fog has cleared.',
  },
]

export const trustNote = {
  title: 'How we vet what goes on this page',
  body: 'Everything in this section gets called or visited before it is listed, and re-checked every quarter — because a dead phone number on a postpartum resource is worse than no listing at all. Sliding-scale and free options are marked as such and are always listed first within their category. Sponsors can never appear in these lists; paid placements on this page are labeled and sit on their own.',
}
